import {
  CreateMessageTemplateDto,
  CreateMessageTemplateSchema,
  MessageTemplateAdminDto,
  MessageTemplateCategoryDto,
  UpdateMessageTemplateDto,
  UpdateMessageTemplateSchema,
} from '@act/shared/data-transfer-objects';
import { useForm } from 'react-hook-form';
import { useApiCall } from 'redux/utils/api-hook';
import { messageTemplatesRequests } from 'redux/requests/message-templates.requests';
import { useEffect, useMemo } from 'react';
import { useAppDispatch } from 'redux/store-hooks';
import {
  createMessageTemplateSuccess,
  updateMessageTemplateSuccess,
} from 'redux/slices/messageTemplatesSlice';
import { clientAppSelectors } from 'redux/slices/clientSlice';
import { zodResolver } from '@hookform/resolvers/zod';
import { useToast } from '../../../../app/ReusableToast';
import { AdminDrawerForm } from '../../AdminDrawer';
import { FormBuilder } from '@act/storybook';
import { FormConfig } from 'stories/forms/FormBuilder/FormBuilder.types';
import useUnsavedChangesDialog from 'react-components/dialog-forms/hooks/use-unsaved-changes-dialog';

interface Props {
  setOpen: (value: boolean) => void;
  open: boolean;
  categories: MessageTemplateCategoryDto[];
  selectedTemplate?: MessageTemplateAdminDto;
  isEdit: boolean;
}

export const MessageTemplateCreateEdit = ({
  setOpen,
  open,
  categories,
  isEdit,
  selectedTemplate,
}: Props) => {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const handleUnsavedChanges = useUnsavedChangesDialog();

  const schema = isEdit
    ? UpdateMessageTemplateSchema
    : CreateMessageTemplateSchema;

  const { control, handleSubmit, formState } = useForm<
    CreateMessageTemplateDto | UpdateMessageTemplateDto
  >({
    defaultValues: selectedTemplate || {
      template: '',
      categoryId: undefined,
      sortOrder: undefined,
      template_clients: [],
      allClients: false,
    },
    resolver: zodResolver(schema),
  });

  const [createMessageTemplateResult, , createMessageTemplate] = useApiCall(
    messageTemplatesRequests.createMessageTemplate
  );
  const [updateMessageTemplateResult, _isUpdating, updateMessageTemplate] =
    useApiCall(messageTemplatesRequests.updateMessageTemplate);
  const allClients = clientAppSelectors.useSelectAllClients();

  const clientList = useMemo(
    () => allClients.ids.map(id => allClients.entities[id]),
    [allClients]
  );
  const clientOptions = useMemo(() => {
    return clientList.map(client => ({
      label: client.name,
      value: client.id,
    }));
  }, [clientList]);
  const categoryOptions = useMemo(() => {
    return categories.map(category => ({
      label: category.name,
      value: category.id,
    }));
  }, [categories]);

  const formConfig: FormConfig<CreateMessageTemplateDto> = [
    {
      type: 'input',
      name: 'template',
      label: 'Template',
      input: 'text',
      props: {
        multiline: true,
      },
    },
    {
      type: 'input',
      name: 'categoryId',
      label: 'Category',
      input: 'select',
      props: {
        options: categoryOptions,
        fullWidth: true,
      },
    },
    {
      type: 'input',
      name: 'sortOrder',
      label: 'Sort Order',
      input: 'number',
    },
    {
      type: 'input',
      name: 'template_clients',
      label: 'Select Clients',
      input: 'autocomplete',
      props: {
        options: clientOptions,
        getOptionLabel: (option: any) => option.label,
        multiple: true,
      },
    },
    {
      type: 'input',
      name: 'allClients',
      label: 'All Clients',
      input: 'checkbox',
    },
  ];

  const onSubmit = (
    data: CreateMessageTemplateDto | UpdateMessageTemplateDto
  ) => {
    if (isEdit) {
      void updateMessageTemplate({
        params: { id: Number(selectedTemplate.id) },
        body: { ...data, sortOrder: Number(data.sortOrder) },
      });
    } else {
      void createMessageTemplate({
        params: null,
        body: {
          ...data,
          sortOrder: Number(data.sortOrder),
        },
      });
    }
  };

  useEffect(() => {
    if (createMessageTemplateResult || updateMessageTemplateResult) {
      if (createMessageTemplateResult) {
        dispatch(createMessageTemplateSuccess(createMessageTemplateResult));
        toast('Message template created successfully');
      }
      if (updateMessageTemplateResult) {
        dispatch(updateMessageTemplateSuccess(updateMessageTemplateResult));
        toast('Message template updated successfully');
      }
      setOpen(false);
    }
  }, [createMessageTemplateResult, updateMessageTemplateResult]);

  const { isDirty } = formState;
  const handleClose = () => {
    if (isDirty) {
      void handleUnsavedChanges(() => {
        setOpen(false);
      });
    } else {
      setOpen(false);
    }
  };

  const DrawerContent = useMemo(
    () => (
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
    ),
    [control, formConfig, formState.errors]
  );

  return (
    <AdminDrawerForm
      title={isEdit ? `Edit Template` : `Add New Template`}
      children={DrawerContent}
      onSubmit={handleSubmit(onSubmit)}
      open={open}
      setOpen={handleClose}
    />
  );
};
